import React, { useState, useEffect } from 'react';
import { ShoppingBag, Check, RefreshCw } from 'lucide-react';
import { fetchProductsFromBackend, getProductImageUrl } from '../../services/api';

const ProductShowcaseAlternating = ({ cartItems = [], onAddToCart, onUpdateQuantity, onBuyNow }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const loadProducts = async () => {
      try {
        const backendProducts = await fetchProductsFromBackend();
        if (isMounted) {
          if (backendProducts && Array.isArray(backendProducts) && backendProducts.length > 0) {
            setProducts(backendProducts);
          } else {
            setProducts([]);
          }
        }
      } catch (e) {
        if (isMounted) setProducts([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadProducts();
    return () => { isMounted = false; };
  }, []);

  if (!loading && products.length === 0) {
    return null;
  }

  return (
    <section className="product-showcase-section" style={{ background: '#ffffff', padding: '60px 56px' }}>
      <div className="product-showcase-container" style={{ maxWidth: '1320px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <span className="section-subhead-red">DYNAMIC PRODUCT CATALOG</span>
          <h2 className="section-title-bold" style={{ fontSize: '40px' }}>
            ENGINEERED FOR RESULTS.<br />
            <span style={{ color: '#dc2626' }}>CLINICALLY PROVEN PANELS.</span>
          </h2>
          <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '560px', margin: '12px auto 0' }}>
            Showing real product models, live prices, and technical specs.
          </p>
        </div>

        {/* 4 Products Alternating Rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
          {products.slice(0, 4).map((p, idx) => {
            const isLeft = idx % 2 === 0;
            const itemPrice = Number(p.price || 350);
            const itemSalePrice = p.sale_price ? Number(p.sale_price) : null;
            const imgUrl = p.image_url ? getProductImageUrl(p.image_url) : getProductImageUrl('/uploads/panel_72led.jpg');
            const availableStock = p.stock !== undefined ? p.stock : 25;

            // Check item quantity in cart
            const cartItem = cartItems.find((item) => item.id === p.id);
            const currentQty = cartItem ? cartItem.quantity : 0;
            const isMaxStockReached = currentQty >= availableStock;

            const productSpecs = [
              `${p.led_chips_count || 144} Dual-Chip 660nm & 850nm LEDs`,
              `${p.actual_power || '240W'} Actual Power Output`,
              `Irradiance ${p.irradiance || '≥ 1400W/m² @ 6 inches'}`,
              `Dimensions ${p.dimensions || '640*230*65MM'}`,
              `Control: ${p.control_interface || 'Touch Screen Interface'}`
            ];

            return (
              <div
                key={p.id || idx}
                className="product-alternating-row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: isLeft ? '0.95fr 1.05fr' : '1.05fr 0.95fr',
                  gap: '56px',
                  alignItems: 'center',
                  background: 'transparent',
                  borderRadius: '0px',
                  padding: '24px 0',
                  border: 'none',
                  borderBottom: idx < 3 ? '1px solid #f1f5f9' : 'none'
                }}
              >
                {/* Product Image Column */}
                <div
                  style={{
                    order: isLeft ? 1 : 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px'
                  }}
                >
                  <img
                    src={imgUrl}
                    alt={p.name}
                    style={{
                      maxHeight: '420px',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      display: 'block',
                      borderRadius: '12px'
                    }}
                    onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_72led.jpg'); }}
                  />
                </div>

                {/* Product Details Content Column */}
                <div style={{ order: isLeft ? 2 : 1 }}>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 800,
                      letterSpacing: '0.15em',
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      display: 'block',
                      marginBottom: '8px'
                    }}
                  >
                    SKU: {p.sku || `IE-${p.id}`}
                  </span>

                  <h3 style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', marginBottom: '12px', lineHeight: 1.2 }}>
                    {p.name}
                  </h3>

                  {/* Price Tag & Availability Badge */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '28px', fontWeight: 900, color: '#dc2626' }}>
                      £{(itemSalePrice && itemSalePrice < itemPrice ? itemSalePrice : itemPrice).toFixed(2)}
                    </span>
                    {itemSalePrice && itemSalePrice < itemPrice && (
                      <span style={{ fontSize: '18px', color: '#94a3b8', textDecoration: 'line-through' }}>
                        £{itemPrice.toFixed(2)}
                      </span>
                    )}
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '3px 10px', borderRadius: '9999px' }}>
                      In Stock
                    </span>
                  </div>

                  <p style={{ fontSize: '14.5px', color: '#475569', lineHeight: 1.6, marginBottom: '24px' }}>
                    {p.description || "Advanced Red Light Therapy panel engineered with dual-chip 660nm Red and 850nm Near-Infrared wavelengths for optimal recovery and wellness."}
                  </p>

                  {/* Specs List */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '32px' }}>
                    {productSpecs.map((spec, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Check size={13} />
                        </div>
                        <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#334155' }}>{spec}</span>
                      </div>
                    ))}
                  </div>

                  {/* 2 CTA BUTTONS: BUY NOW & ADD TO CART OR QUANTITY SELECTOR (+ -) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                    {currentQty > 0 ? (
                      /* IF ALREADY IN CART: HIDE BUY NOW & ADD TO CART, SHOW - N IN CART + */
                      <div style={{ display: 'inline-flex', alignItems: 'center', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '9999px', padding: '6px 16px', gap: '14px' }}>
                        <button
                          onClick={() => onUpdateQuantity && onUpdateQuantity(p.id, -1, availableStock)}
                          style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', fontWeight: 900, fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          -
                        </button>
                        <span style={{ fontSize: '15px', fontWeight: 900, color: '#dc2626' }}>
                          {currentQty} in Cart
                        </span>
                        <button
                          onClick={() => onUpdateQuantity && onUpdateQuantity(p.id, 1, availableStock)}
                          disabled={currentQty >= availableStock}
                          style={{ width: '32px', height: '32px', borderRadius: '50%', background: currentQty >= availableStock ? '#f1f5f9' : '#dc2626', border: 'none', color: currentQty >= availableStock ? '#94a3b8' : '#ffffff', fontWeight: 900, fontSize: '18px', cursor: currentQty >= availableStock ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      /* IF NOT IN CART: SHOW BUY NOW & ADD TO CART */
                      <>
                        <button
                          className="btn-red-pill"
                          style={{ padding: '14px 28px', fontSize: '14px', background: '#dc2626', color: '#ffffff', borderRadius: '9999px', fontWeight: 800, border: 'none', cursor: 'pointer' }}
                          onClick={() => onBuyNow && onBuyNow(p)}
                        >
                          <span>BUY NOW</span>
                        </button>

                        <button
                          style={{
                            padding: '14px 28px',
                            fontSize: '14px',
                            background: '#ffffff',
                            color: '#0f172a',
                            border: '1px solid #cbd5e1',
                            borderRadius: '9999px',
                            fontWeight: 800,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                          }}
                          onClick={() => onAddToCart && onAddToCart(p)}
                        >
                          <ShoppingBag size={17} />
                          <span>ADD TO CART</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcaseAlternating;
